/* ==========================================================================
   Raptor Racing brand layer, item 4: collection filters (handoff kit 6C).
   27 Sept 2026. ES5 IIFE, no dependencies, loaded with defer from
   layout/theme.liquid after rr-filters.css.

   The filters are XCloud's. This file enhances the DOM XCloud injects into
   #cloud_search_filters_sidebar (desktop), #cloud_search_filters_root (the
   phone Filter link) and .cloud-search-filters-pane (the phone pane XCloud
   appends to body). It never replaces XCloud's controls: checkbox clicks,
   price inputs, the slider, the value-search fields, the Filter link and
   the pane close all keep XCloud's own handlers. What it adds:

     1. collapsible groups: every non-heading child of .cloud-search-filter
        is wrapped in div.rr-gb > div.rr-gi; the heading toggles .rr-shut on
        the group (role=button, tabindex, aria-expanded, Enter and Space);
        the state is remembered per group name in sessionStorage
        rr-filters-state; Product type and Wheel* groups start closed
     2. counts without parentheses
     3. Show more for lists over 6 rows (revealed again, and the button
        dropped, when the user types in that group's value-search field)
     4. the collection Search placeholder "Search <title> parts"
     5. applied filter chips moved from the sidebar into div.rr-active-filters
        above the product grid, with a fallback when XCloud's handler does
        not follow the chip out of the sidebar
     6. the phone Filter link rewritten to "Filters (N)"; it is never moved
        (XCloud may delegate its open-pane handler to the container it
        renders into). When there are chips a proxy button in the active
        row carries the same text and forwards its click to the link, and
        the link's controls block is hidden meanwhile (data-rr-proxied)
     7. a pinned "Show N results" footer in XCloud's pane that closes the
        pane and scrolls to the grid

   Everything is idempotent (data-rr-enhanced on each enhanced element) and
   re-runs when XCloud re-renders the sidebar (MutationObserver on the
   mount). Reduced motion: the collapse transition is zeroed in CSS; the only
   JS motion is the scroll after the pane closes, which checks matchMedia.
   The whitespace minifier strips inline scripts containing " <", so all JS
   lives here and there are no HTML strings with a leading angle bracket.
   ========================================================================== */
(function () {
  'use strict';

  var MOUNT_ID = 'cloud_search_filters_sidebar';
  var STORE_KEY = 'rr-filters-state';
  var SHOW_ROWS = 6;
  var MINUS = '−';
  var enhancedOnce = false;
  var stateCache = null;

  function q(sel, root) {
    try { return (root || document).querySelector(sel); } catch (e) { return null; }
  }

  function qa(sel, root) {
    try { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); } catch (e) { return []; }
  }

  function text(el) {
    return el ? String(el.textContent || '').replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '') : '';
  }

  function hasClass(el, cls) {
    return !!(el && el.classList && el.classList.contains(cls));
  }

  function closest(el, sel) {
    while (el && el.nodeType === 1) {
      try { if (el.matches(sel)) { return el; } } catch (e) { return null; }
      el = el.parentNode;
    }
    return null;
  }

  function reducedMotion() {
    try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; }
  }

  function formatCount(n) {
    /* v2.18 6L D1/D2: the app caps its totals (25,001 and "Page 1 of 834"
       on the biggest collections), so a capped total reads 25,000+ */
    if (n === 25001) { return '25,000+'; }
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* ---------- open/closed state ---------- */

  function readState() {
    if (stateCache) { return stateCache; }
    stateCache = {};
    try {
      var raw = window.sessionStorage.getItem(STORE_KEY);
      if (raw) { stateCache = JSON.parse(raw) || {}; }
    } catch (e) { stateCache = {}; }
    return stateCache;
  }

  function writeState(name, open) {
    var s = readState();
    s[name] = open ? 'open' : 'shut';
    try { window.sessionStorage.setItem(STORE_KEY, JSON.stringify(s)); } catch (e) { /* private mode */ }
  }

  function groupName(group) {
    var name = group.getAttribute('data-filter-name') || '';
    if (!name) { name = text(q('.cloud-search-filter__name', group)); }
    return name;
  }

  function groupLabel(group) {
    var heading = q('.cloud-search-filter__name', group);
    var label = '';
    if (heading) {
      /* the heading holds the collapse span plus a text span */
      var spans = qa(':scope > span', heading);
      for (var i = 0; i < spans.length; i++) {
        if (!hasClass(spans[i], 'cloud-search-filter__collapse') && text(spans[i])) { label = text(spans[i]); break; }
      }
      if (!label) { label = text(heading); }
    }
    return label;
  }

  function defaultOpen(label) {
    var l = label.toLowerCase();
    if (l === 'product type') { return false; }
    if (l.indexOf('wheel') === 0) { return false; }
    return true;
  }

  function setOpen(group, open, remember) {
    var heading = q('.cloud-search-filter__name', group);
    if (open) { group.classList.remove('rr-shut'); } else { group.classList.add('rr-shut'); }
    if (heading) { heading.setAttribute('aria-expanded', open ? 'true' : 'false'); }
    if (remember) { writeState(groupName(group), open); }
  }

  /* ---------- 1. wrap the body and make the heading a toggle ---------- */

  // XCloud's typing boxes (the Search field, a list's "Search options" box, the price inputs)
  // are left where XCloud put them. XCloud puts a moved text field back on the first keystroke,
  // and moving a focused input drops the cursor out of it (owner, 15 Oct 2026: the first click
  // in the collection Search box lost the cursor after one letter). They come first in their
  // group, so they stay between the heading and .rr-gb; rr-filters.css hides them when shut.
  function holdsTyping(el) {
    if (!el || el.nodeType !== 1) { return false; }
    if (hasClass(el, 'cloud-search-text-field') || hasClass(el, 'cloud-search-range-inputs')) { return true; }
    return !!q('input[type="search"], input[type="text"], input[type="number"]', el);
  }

  function wrapGroup(group) {
    if (q(':scope > .rr-gb', group)) { return; }
    var heading = q(':scope > .cloud-search-filter__name', group);
    var gb = document.createElement('div');
    gb.className = 'rr-gb';
    var gi = document.createElement('div');
    gi.className = 'rr-gi';
    gb.appendChild(gi);
    var kids = Array.prototype.slice.call(group.childNodes);
    for (var i = 0; i < kids.length; i++) {
      var k = kids[i];
      if (k === heading) { continue; }
      if (k.nodeType === 3 && !text(k)) { group.removeChild(k); continue; }
      if (holdsTyping(k)) { continue; }
      gi.appendChild(k);
    }
    group.appendChild(gb);
  }

  function bindHeading(group) {
    var heading = q(':scope > .cloud-search-filter__name', group);
    if (!heading || heading.getAttribute('data-rr-enhanced')) { return; }
    heading.setAttribute('data-rr-enhanced', '1');
    heading.setAttribute('role', 'button');
    heading.setAttribute('tabindex', '0');
    heading.addEventListener('keydown', function (ev) {
      var key = ev.key || ev.keyCode;
      if (key === 'Enter' || key === ' ' || key === 'Spacebar' || key === 13 || key === 32) {
        ev.preventDefault();
        setOpen(group, hasClass(group, 'rr-shut'), true);
      }
    });
  }

  /* The click is caught in the capture phase on the enhanced root so it
     never reaches XCloud's own listeners on the heading (its desktop
     heading does nothing, its pane heading must not fight the theme).
     Checkbox clicks are not inside the heading and are left alone. */
  function bindRootClicks(root) {
    if (root.getAttribute('data-rr-clicks')) { return; }
    root.setAttribute('data-rr-clicks', '1');
    root.addEventListener('click', function (ev) {
      var heading = closest(ev.target, '.cloud-search-filter__name');
      if (!heading) { return; }
      var group = closest(heading, '.cloud-search-filter');
      if (!group || !q(':scope > .rr-gb', group)) { return; }
      ev.preventDefault();
      ev.stopPropagation();
      setOpen(group, hasClass(group, 'rr-shut'), true);
    }, true);
  }

  /* ---------- 2. counts ---------- */

  function stripCounts(group) {
    var counts = qa('.cloud-search-filter-value__count', group);
    for (var i = 0; i < counts.length; i++) {
      var t = text(counts[i]);
      var m = t.match(/^\(\s*([\d,\.\s]+)\s*\)$/);
      if (m) { counts[i].textContent = m[1].replace(/\s+/g, ''); }
    }
    /* the non-breaking space XCloud prints between name and count would be
       an anonymous flex item; drop it */
    var labels = qa('label.cloud-search-filter-value', group);
    for (var j = 0; j < labels.length; j++) {
      var nodes = Array.prototype.slice.call(labels[j].childNodes);
      for (var k = 0; k < nodes.length; k++) {
        if (nodes[k].nodeType === 3 && !String(nodes[k].nodeValue).replace(/[\s ]+/g, '')) {
          labels[j].removeChild(nodes[k]);
        }
      }
    }
  }

  /* ---------- 3. show more ---------- */

  function revealAll(group) {
    var hidden = qa('label.cloud-search-filter-value.rr-hidden', group);
    for (var i = 0; i < hidden.length; i++) { hidden[i].classList.remove('rr-hidden'); }
    var btn = q('.rr-more', group);
    if (btn && btn.parentNode) { btn.parentNode.removeChild(btn); }
    group.setAttribute('data-rr-more', 'done');
  }

  function showMore(group) {
    if (group.getAttribute('data-rr-more')) { return; }
    var labels = qa('label.cloud-search-filter-value', group);
    if (labels.length <= SHOW_ROWS) { return; }
    group.setAttribute('data-rr-more', '1');
    var extra = [];
    var shown = 0;
    for (var i = 0; i < labels.length; i++) {
      var selected = hasClass(labels[i], 'cloud-search-filter-value--selected') || !!q('input:checked', labels[i]);
      if (selected || shown < SHOW_ROWS) { shown++; continue; }
      extra.push(labels[i]);
    }
    if (!extra.length) { return; }
    for (var j = 0; j < extra.length; j++) { extra[j].classList.add('rr-hidden'); }

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'rr-more';
    btn.setAttribute('aria-expanded', 'false');
    btn.textContent = '+ Show ' + extra.length + ' more';
    var open = false;
    btn.addEventListener('click', function (ev) {
      ev.preventDefault();
      ev.stopPropagation();
      open = !open;
      for (var k = 0; k < extra.length; k++) {
        if (open) { extra[k].classList.remove('rr-hidden'); } else { extra[k].classList.add('rr-hidden'); }
      }
      btn.textContent = open ? (MINUS + ' Show less') : ('+ Show ' + extra.length + ' more');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    var gi = q(':scope > .rr-gb > .rr-gi', group) || group;
    gi.appendChild(btn);

    /* typing in the group's value-search field: XCloud filters the rows, so
       every row must be eligible; the button goes away for good. Only the
       input event (typing, paste, IME): a keydown listener would fire on
       Tab, Shift or Escape and reveal the list for a keyboard user merely
       passing through the field. 27 Sept 2026. */
    var search = q('.cloud-search-text-field--value-search input', group);
    if (search) {
      search.addEventListener('input', function () { revealAll(group); });
    }
  }

  /* ---------- 4. collection search placeholder ---------- */

  function collectionTitle() {
    var main = q('[data-collection-main]');
    var t = main ? (main.getAttribute('data-rr-collection-title') || '') : '';
    if (!t) {
      var h1 = q('h1');
      t = text(h1);
    }
    t = t.replace(/^\s+|\s+$/g, '');
    if (!t || t.toLowerCase() === 'search' || t.toLowerCase() === 'search results') { return ''; }
    return t;
  }

  function setSearchPlaceholder(root) {
    /* only the collection Search group's field: never the value-search
       fields above long lists and never the two Price fields, which share
       the .cloud-search-text-field__input class */
    var fields = qa('.cloud-search-text-field:not(.cloud-search-text-field--value-search):not(.cloud-search-num-field) input.cloud-search-text-field__input', root);
    for (var i = 0; i < fields.length; i++) {
      var input = fields[i];
      if (hasClass(input, 'cloud-search-text-field__input--value-search') || hasClass(input, 'cloud-search-num-field__input')) { continue; }
      if (input.getAttribute('data-rr-enhanced')) { continue; }
      var type = (input.getAttribute('type') || 'text').toLowerCase();
      if (type !== 'search' && type !== 'text') { continue; }
      var group = closest(input, '.cloud-search-filter');
      var gl = group ? groupLabel(group).toLowerCase() : '';
      var isSearchGroup = type === 'search' || gl === 'search' || gl.indexOf('search') === 0 || /search collection/i.test(input.getAttribute('placeholder') || '');
      if (!isSearchGroup) { continue; }
      input.setAttribute('data-rr-enhanced', '1');
      var title = collectionTitle();
      input.setAttribute('placeholder', title ? ('Search ' + title + ' parts') : 'Search these results');
    }
  }

  /* ---------- 5. applied filters above the grid ---------- */

  function gridAnchor() {
    /* returns {parent, before} for the active row */
    var products = q('.cloud-search-filters-products');
    if (products) { return { parent: products, before: products.firstChild }; }
    var wrapper = q('.collection-matrix__wrapper');
    if (wrapper && wrapper.parentNode) { return { parent: wrapper.parentNode, before: wrapper }; }
    var main = q('[data-collection-main]');
    if (main) { return { parent: main, before: main.firstChild }; }
    return null;
  }

  function activeRow(create) {
    var row = q('.rr-active-filters');
    if (row || !create) { return row; }
    var anchor = gridAnchor();
    if (!anchor) { return null; }
    row = document.createElement('div');
    row.className = 'rr-active-filters';
    row.setAttribute('data-rr-enhanced', '1');
    var label = document.createElement('span');
    label.className = 'rr-active-filters__label';
    label.textContent = 'Active filters';
    row.appendChild(label);
    anchor.parent.insertBefore(row, anchor.before || null);
    return row;
  }

  function chipCount() {
    var chips = qa('.rr-active-filters .cloud-search-applied-filter, #' + MOUNT_ID + ' .cloud-search-applied-filter, .cloud-search-filters-sidebar .cloud-search-applied-filter');
    if (chips.length) { return chips.length; }
    var n = 0;
    try {
      var qs = String(window.location.search || '').replace(/^\?/, '');
      if (qs) {
        var parts = qs.split('&');
        for (var i = 0; i < parts.length; i++) {
          var key = decodeURIComponent(parts[i].split('=')[0] || '');
          if (key.indexOf('filter') === 0) { n++; }
        }
      }
    } catch (e) { n = 0; }
    return n;
  }

  /* Fallback for a chip whose XCloud handler did not follow it out of the
     sidebar: click the matching selected checkbox (XCloud navigates), or,
     for a price chip, drop the matching filter_ params from the URL. */
  function normalise(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  function fallbackRemove(chip) {
    var name = normalise(text(q('.cloud-search-applied-filter__name', chip)));
    var value = normalise(text(q('.cloud-search-applied-filter__values', chip)));
    var groups = qa('#' + MOUNT_ID + ' .cloud-search-filter, .cloud-search-filters-sidebar .cloud-search-filter, .cloud-search-filters-pane .cloud-search-filter');
    for (var i = 0; i < groups.length; i++) {
      if (name && normalise(groupLabel(groups[i])) !== name) { continue; }
      var labels = qa('label.cloud-search-filter-value', groups[i]);
      for (var j = 0; j < labels.length; j++) {
        var input = q('input[type="checkbox"]', labels[j]);
        if (!input || !input.checked) { continue; }
        if (normalise(text(q('.cloud-search-filter-value__name', labels[j]))) === value) { input.click(); return true; }
      }
    }
    /* URL fallback: remove every filter_ param whose key contains the chip name */
    try {
      var qs = String(window.location.search || '').replace(/^\?/, '');
      if (!qs || !name) { return false; }
      var keep = [];
      var removed = false;
      var parts = qs.split('&');
      for (var k = 0; k < parts.length; k++) {
        var key = decodeURIComponent(parts[k].split('=')[0] || '');
        if (key.indexOf('filter') === 0 && normalise(key).indexOf(name) !== -1) { removed = true; continue; }
        keep.push(parts[k]);
      }
      if (!removed) { return false; }
      window.location.href = window.location.pathname + (keep.length ? '?' + keep.join('&') : '') + window.location.hash;
      return true;
    } catch (e) { return false; }
  }

  function fallbackClearAll() {
    try {
      var qs = String(window.location.search || '').replace(/^\?/, '');
      var keep = [];
      var parts = qs ? qs.split('&') : [];
      for (var i = 0; i < parts.length; i++) {
        var key = decodeURIComponent(parts[i].split('=')[0] || '');
        if (key === 'q' || key === 'sort_by') { keep.push(parts[i]); }
      }
      window.location.href = window.location.pathname + (keep.length ? '?' + keep.join('&') : '');
      return true;
    } catch (e) { return false; }
  }

  function guardChipClicks(row) {
    if (row.getAttribute('data-rr-guard')) { return; }
    row.setAttribute('data-rr-guard', '1');
    row.addEventListener('click', function (ev) {
      var clear = closest(ev.target, '.cloud-search-applied-filter__clear');
      var chip = clear ? closest(clear, '.cloud-search-applied-filter') : closest(ev.target, '.cloud-search-applied-filter');
      var all = closest(ev.target, '.cloud-search-clear-filters');
      if (!chip && !all) { return; }
      if (all && all.tagName === 'A' && all.getAttribute('href') && all.getAttribute('href') !== '#') { return; }
      var leaving = false;
      var onLeave = function () { leaving = true; };
      window.addEventListener('beforeunload', onLeave);
      window.setTimeout(function () {
        window.removeEventListener('beforeunload', onLeave);
        if (leaving) { return; }
        if (chip && chip.parentNode && document.body.contains(chip) && document.body.contains(row)) {
          fallbackRemove(chip);
        } else if (all && document.body.contains(all)) {
          fallbackClearAll();
        }
      }, 700);
    }, false);
  }

  function moveApplied(root) {
    var applied = q('.cloud-search-filters-sidebar__applied-filters', root);
    if (!applied || hasClass(root, 'cloud-search-filters-pane')) { return; }
    var row = activeRow(true);
    if (!row) { return; }
    if (applied.parentNode !== row) {
      /* XCloud re-rendered the sidebar: drop the block moved earlier */
      var stale = qa('.cloud-search-filters-sidebar__applied-filters, .cloud-search-clear-filters, .cloud-search-filters-sidebar__separator', row);
      for (var s = 0; s < stale.length; s++) {
        if (stale[s] !== applied && stale[s].parentNode) { stale[s].parentNode.removeChild(stale[s]); }
      }
      row.appendChild(applied);
    }
    /* XCloud prints Clear and the separator after the chips block */
    var clear = q('.cloud-search-clear-filters', root);
    if (clear) {
      /* the mock reads "Clear all"; XCloud prints "Clear" */
      if (!clear.children.length && /^clear(\s+filters)?$/i.test(text(clear))) { clear.textContent = 'Clear all'; }
      row.appendChild(clear);
    }
    var sep = q('.cloud-search-filters-sidebar__separator', root);
    if (sep) { row.appendChild(sep); }
    guardChipClicks(row);
  }

  /* ---------- 6. the phone Filter link ---------- */

  /* XCloud's anchor stays where XCloud rendered it: only its text span,
     classes and aria-label change. Re-parenting it would lose a click
     handler XCloud delegates to .cloud-search-page-controls or
     #cloud_search_filters_root, and the pane could not open on a filtered
     page. The mock's right-of-row placement is a theme proxy button instead:
     its click forwards to the anchor with element.click(), which reaches
     delegated handlers, and the anchor's controls block is hidden while the
     proxy exists (data-rr-proxied, rr-filters.css section 8). 27 Sept 2026. */
  function xcloudLink() {
    return q('#cloud_search_filters_root a.cloud-search-page-controls__filters-link') || q('a.cloud-search-page-controls__filters-link');
  }

  function syncProxy(link, txt, aria, n) {
    var row = activeRow(false);
    var controls = closest(link, '.cloud-search-page-controls') || link.parentNode;
    var proxy = row ? q('.rr-filters-open', row) : null;
    if (!row || n < 1) {
      if (proxy && proxy.parentNode) { proxy.parentNode.removeChild(proxy); }
      if (controls && controls.nodeType === 1) { controls.removeAttribute('data-rr-proxied'); }
      return;
    }
    if (!proxy) {
      proxy = document.createElement('button');
      proxy.type = 'button';
      proxy.className = 'rr-button rr-button--primary rr-filters-open';
      proxy.addEventListener('click', function (ev) {
        ev.preventDefault();
        var target = xcloudLink();
        if (target) { target.click(); }
      });
    }
    if (text(proxy) !== txt) { proxy.textContent = txt; }
    proxy.setAttribute('aria-label', aria);
    /* keep it last so its margin-left: auto pushes it to the row's right,
       after whatever moveApplied appended since */
    if (row.lastChild !== proxy) { row.appendChild(proxy); }
    if (controls && controls.nodeType === 1) { controls.setAttribute('data-rr-proxied', '1'); }
  }

  function enhanceLink() {
    var links = qa('a.cloud-search-page-controls__filters-link');
    var n = chipCount();
    var txt = n > 0 ? ('Filters (' + n + ')') : 'Filters';
    var aria = n > 0 ? ('Open filters, ' + n + ' applied') : 'Open filters';
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      var label = q('.cloud-search-filters-link-text', link);
      if (label) {
        if (text(label) !== txt) { label.textContent = txt; }
      } else if (text(link) !== txt) {
        link.textContent = txt;
      }
      link.setAttribute('aria-label', aria);
      if (!link.getAttribute('data-rr-enhanced')) {
        link.setAttribute('data-rr-enhanced', '1');
        link.classList.add('rr-button');
        link.classList.add('rr-button--primary');
      }
    }
    if (links.length) { syncProxy(links[0], txt, aria, n); }
  }

  /* ---------- 7. the pane footer ---------- */

  /* XCloud's own count first (the filtered number it prints on /a/search
     pages in an element whose class contains results-count), then the
     unfiltered collection.products_count the theme prints on
     [data-collection-main]; a proxy page that renders both must not show the
     unfiltered total. The theme's .search__results-count exists only on the
     native /search page, which has no XCloud mounts. 27 Sept 2026. */
  function resultsCount() {
    if (!isNaN(FOUND)) { return FOUND; }
    var raw = text(q('[class*="results-count"]'));
    if (!/\d/.test(raw)) {
      var main = q('[data-collection-main]');
      raw = main ? (main.getAttribute('data-rr-results-count') || '') : '';
    }
    var m = String(raw || '').replace(/,/g, '').match(/\d+/);
    return m ? parseInt(m[0], 10) : NaN;
  }

  function scrollToGrid() {
    var target = q('.rr-active-filters') || q('#cloud_search_filters_root') || q('.cloud-search-filters-products') || q('.collection-matrix__wrapper') || q('[data-collection-main]');
    if (!target) { return; }
    try {
      target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    } catch (e) {
      try { target.scrollIntoView(true); } catch (e2) { /* nothing */ }
    }
  }

  function addPaneFooter(pane) {
    if (q('.rr-drawer__foot', pane)) { return; }
    var foot = document.createElement('div');
    foot.className = 'rr-drawer__foot';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'rr-button rr-button--primary rr-drawer__apply';
    var n = resultsCount();
    btn.textContent = isNaN(n) ? 'Show results' : ('Show ' + formatCount(n) + ' results');
    btn.addEventListener('click', function (ev) {
      ev.preventDefault();
      if (batchDirty()) {
        btn.disabled = true;
        btn.textContent = 'Loading results';
        window.location.href = batchUrl();
        return;
      }
      var close = q('.cloud-search-filters-pane__close', pane);
      if (close) {
        close.click();
      } else {
        var backdrop = q('.cloud-search-filters-pane-backdrop');
        if (backdrop) { backdrop.click(); }
      }
      window.setTimeout(scrollToGrid, 50);
    });
    foot.appendChild(btn);
    pane.appendChild(foot);
    var close = q('.cloud-search-filters-pane__close', pane);
    if (close && !close.getAttribute('aria-label')) {
      close.setAttribute('aria-label', 'Close filters');
      close.setAttribute('role', 'button');
      close.setAttribute('tabindex', '0');
    }
  }

  /* ---------- enhance(root) ---------- */

  function enhanceGroup(group) {
    if (hasClass(group, 'cloud-search-filter--rounded')) { return; }
    if (!q(':scope > .cloud-search-filter__name', group)) { return; }
    var fresh = !group.getAttribute('data-rr-enhanced');
    wrapGroup(group);
    bindHeading(group);
    stripCounts(group);
    showMore(group);
    if (fresh) {
      group.setAttribute('data-rr-enhanced', '1');
      var label = groupLabel(group);
      var saved = readState()[groupName(group)];
      var open = saved ? saved === 'open' : defaultOpen(label);
      /* a group with a selected value is always worth seeing */
      if (!open && q('.cloud-search-filter-value--selected, input:checked', group)) { open = true; }
      setOpen(group, open, false);
    }
  }

  function enhance(root) {
    if (!root) { return; }
    bindRootClicks(root);
    var groups = qa('.cloud-search-filter', root);
    for (var i = 0; i < groups.length; i++) {
      try { enhanceGroup(groups[i]); } catch (e) { /* keep going */ }
    }
    try { setSearchPlaceholder(root); } catch (e) { /* nothing */ }
    try { moveApplied(root); } catch (e) { /* nothing */ }
    try { enhanceLink(); } catch (e) { /* nothing */ }
    if (hasClass(root, 'cloud-search-filters-pane')) {
      try { addPaneFooter(root); } catch (e) { /* nothing */ }
    }
    root.setAttribute('data-rr-enhanced', '1');
    enhancedOnce = true;
  }

  /* ---------- boot: wait for XCloud ---------- */

  function sidebarRoot() {
    var mount = document.getElementById(MOUNT_ID);
    var inner = mount ? q('.cloud-search-filters-sidebar', mount) : null;
    if (inner) { return inner; }
    if (mount && q('.cloud-search-filter', mount)) { return mount; }
    return q('.cloud-search-filters-sidebar');
  }

  function enhanceSidebar() {
    var root = sidebarRoot();
    if (root && q('.cloud-search-filter', root)) { enhance(root); return true; }
    return false;
  }

  function observeMount() {
    if (!window.MutationObserver) { return; }
    var mount = document.getElementById(MOUNT_ID) || q('.cloud-search-filters-sidebar-container') || null;
    if (!mount || mount.getAttribute('data-rr-observed')) { return; }
    mount.setAttribute('data-rr-observed', '1');
    var timer = null;
    var obs = new MutationObserver(function () {
      if (timer) { return; }
      timer = window.setTimeout(function () {
        timer = null;
        try { enhanceSidebar(); } catch (e) { /* nothing */ }
        obs.takeRecords();
      }, 60);
    });
    obs.observe(mount, { childList: true, subtree: true });
  }

  function observeRootControls() {
    if (!window.MutationObserver) { return; }
    var root = document.getElementById('cloud_search_filters_root');
    if (!root || root.getAttribute('data-rr-observed')) { return; }
    root.setAttribute('data-rr-observed', '1');
    var obs = new MutationObserver(function () {
      try { enhanceLink(); } catch (e) { /* nothing */ }
      obs.takeRecords();
    });
    obs.observe(root, { childList: true, subtree: true });
  }

  function paneFrom(node) {
    if (!node || node.nodeType !== 1) { return null; }
    if (hasClass(node, 'cloud-search-filters-pane')) { return node; }
    return q('.cloud-search-filters-pane', node);
  }

  function enhancePanes() {
    var panes = qa('.cloud-search-filters-pane');
    for (var i = 0; i < panes.length; i++) {
      if (q('.cloud-search-filter', panes[i])) { enhance(panes[i]); }
    }
  }

  function observePanes() {
    if (!window.MutationObserver || !document.body) { return; }
    if (document.body.getAttribute('data-rr-pane-observed')) { return; }
    document.body.setAttribute('data-rr-pane-observed', '1');
    var obs = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var pane = paneFrom(added[j]);
          if (pane) {
            /* XCloud fills the pane in the same tick it appends it; give it
               a moment in case it renders asynchronously */
            (function (p) {
              var tries = 0;
              var tick = function () {
                tries++;
                if (q('.cloud-search-filter', p)) { enhance(p); return; }
                if (tries < 20 && document.body.contains(p)) { window.setTimeout(tick, 100); }
              };
              tick();
            })(pane);
          }
        }
      }
    });
    obs.observe(document.body, { childList: true });
    enhancePanes();
  }

  /* ---------- 8. phone pane: tick several, apply once (v2.18 6L D1) ----------
     XCloud navigates on every tick, which closed the pane each time. In the
     phone pane the ticks on list facets (Categories, Brand, Product type,
     the Wheel groups) are held here instead: the box shows the pending
     state, the pinned button reads "Show results", and pressing it loads
     one URL in XCloud's own format (filter_<id>=value, or filter_<id>[i]
     for several). Facet ids come from XCloud's own search response
     (app.cloudsearchapp.com/api/v1/search), read through an XHR hook;
     until they are known, and for Availability, Price and Search, XCloud
     keeps its own behaviour. */
  var FACETS = null;
  var FOUND = NaN;
  var pending = {};
  function learnFacets(obj, depth) {
    if (!obj || typeof obj !== 'object' || depth > 6) { return; }
    if (Object.prototype.toString.call(obj) === '[object Array]') {
      var hits = obj.filter(function (f) { return f && typeof f === 'object' && typeof f.id === 'string' && typeof f.name === 'string' && f.type; });
      if (hits.length >= 2) {
        FACETS = FACETS || {};
        hits.forEach(function (f) { FACETS[f.name] = { id: f.id, type: String(f.type) }; });
        return;
      }
      for (var i = 0; i < obj.length && i < 50; i++) { learnFacets(obj[i], depth + 1); }
      return;
    }
    for (var k in obj) { if (Object.prototype.hasOwnProperty.call(obj, k)) { learnFacets(obj[k], depth + 1); } }
  }
  (function hookXhr() {
    var X = window.XMLHttpRequest;
    if (!X || !X.prototype || X.prototype.rrFacets) { return; }
    var open = X.prototype.open;
    var send = X.prototype.send;
    X.prototype.open = function (method, url) {
      try { this.rrUrl = String(url || ''); } catch (e) { /* ignore */ }
      return open.apply(this, arguments);
    };
    X.prototype.send = function () {
      var xhr = this;
      try {
        if (/cloudsearchapp\.com\/api\/v\d+\/search/.test(xhr.rrUrl || '')) {
          xhr.addEventListener('load', function () {
            try {
              var j = JSON.parse(xhr.responseText);
              learnFacets(j, 0);
              if (typeof j.numFoundProducts === 'number') { FOUND = j.numFoundProducts; showFound(); }
            } catch (e) { /* not JSON */ }
          });
        }
      } catch (e) { /* never break the app's own request */ }
      return send.apply(this, arguments);
    };
    X.prototype.rrFacets = true;
  })();
  window.rrFilterFacets = function () { return FACETS; };
  function batchable(label) {
    if (!FACETS || !label) { return null; }
    var pane = label.closest ? label.closest('.cloud-search-filters-pane') : null;
    if (!pane) { return null; }
    if (!q('input[type="checkbox"]', label)) { return null; }
    if (/^\/collections\/[^\/?#]+\/[^\/?#]+/.test(window.location.pathname)) { return null; }   /* a tag-filtered path: leave it to XCloud */
    var group = label.closest('.cloud-search-filter');
    var name = group ? group.getAttribute('data-filter-name') : '';
    var f = FACETS[name];
    if (!f || /availability|price|range|search|sort/i.test(f.type)) { return null; }
    var value = label.getAttribute('data-filter-value') || text(q('.cloud-search-filter-value__name', label));
    return value ? { id: f.id, value: value } : null;
  }
  function batchDirty() {
    for (var k in pending) { if (pending[k]) { return true; } }
    return false;
  }
  function batchUrl() {
    var params = new URLSearchParams(window.location.search);
    var sel = {};
    var keep = [];
    params.forEach(function (v, k) {
      var m = /^filter_([^\[\]]+)(\[[^\]]*\])?$/.exec(k);
      if (m) { (sel[m[1]] = sel[m[1]] || []).push(v); } else if (k !== 'page') { keep.push([k, v]); }
    });
    Object.keys(pending).forEach(function (key) {
      if (!pending[key]) { return; }
      var cut = key.indexOf('\u0001');
      var id = key.slice(0, cut);
      var val = key.slice(cut + 1);
      var arr = sel[id] || (sel[id] = []);
      var at = arr.indexOf(val);
      if (at >= 0) { arr.splice(at, 1); } else { arr.push(val); }
    });
    var out = new URLSearchParams();
    keep.forEach(function (p) { if (p[0] !== 'country') { out.append(p[0], p[1]); } });
    Object.keys(sel).forEach(function (id) {
      var a = sel[id];
      if (!a.length) { return; }
      if (a.length === 1) { out.append('filter_' + id, a[0]); }
      else { a.forEach(function (v, i) { out.append('filter_' + id + '[' + i + ']', v); }); }
    });
    var country = params.get('country') || (window.Shopify && window.Shopify.country ? String(window.Shopify.country).toLowerCase() : '');
    if (country) { out.append('country', country); }
    var path = window.location.pathname;
    var m2 = /^\/collections\/([^\/?#]+)/.exec(path);
    if (m2) { path = '/a/search/' + m2[1]; }
    return path + '?' + out.toString();
  }
  function syncApply() {
    var btn = q('.cloud-search-filters-pane .rr-drawer__apply');
    if (!btn) { return; }
    if (batchDirty()) { btn.textContent = 'Show results'; }
    else {
      var n = resultsCount();
      btn.textContent = isNaN(n) ? 'Show results' : ('Show ' + formatCount(n) + ' results');
    }
  }
  /* v2.18 6L D2: the real product count ("56,544 products") in the results
     header, from the app's own numFoundProducts; its pagination caps at
     25,001, which reads "25,000+" */
  function showFound() {
    if (isNaN(FOUND)) { return; }
    var host = q('[data-rr-rescount]');
    if (!host) {
      var anchor = q('.rr-active-filters') || q('#cloud_search_filters_root');
      if (!anchor || !anchor.parentNode) { window.setTimeout(showFound, 400); return; }
      host = document.createElement('p');
      host.className = 'rr-rescount';
      host.setAttribute('data-rr-rescount', '');
      host.setAttribute('aria-live', 'polite');
      anchor.parentNode.insertBefore(host, anchor);
    }
    host.textContent = formatCount(FOUND) + (FOUND === 1 ? ' product' : ' products');
    syncApply();
  }

  /* v2.18 6L D3 on the search app's pages (/a/search, filtered collections):
     the app has no Sort option to switch on, but its results honour
     ?sort_by=price-ascending / price-descending (default = best selling), so
     the same Sort field the collection pages have goes above the results.
     "Newest" is not offered here: the app ignores every date sort value. */
  function appSort() {
    if (!/^\/a\/search/.test(window.location.pathname) || q('[data-rr-appsort]')) { return; }
    document.documentElement.classList.add('rr-app-page');
    var anchor = q('[data-rr-rescount]') || q('.rr-active-filters') || q('#cloud_search_filters_root');
    if (!anchor || !anchor.parentNode) { window.setTimeout(appSort, 400); return; }
    var params = new URLSearchParams(window.location.search);
    var cur = params.get('sort_by') || '';
    var box = document.createElement('div');
    box.className = 'collection__sort-by-filter select rr-appsort';
    box.setAttribute('data-rr-appsort', '');
    box.innerHTML = '<label class="rr-sort__label" for="rr-appsort">Sort</label><select class="sort_by" id="rr-appsort"></select>';
    var sel = box.querySelector('select');
    [['', 'Best selling'], ['price-ascending', 'Price, low to high'], ['price-descending', 'Price, high to low']].forEach(function (o) {
      var op = document.createElement('option');
      op.value = o[0];
      op.textContent = o[1];
      if (o[0] === cur) { op.selected = true; }
      sel.appendChild(op);
    });
    sel.addEventListener('change', function () {
      var p = new URLSearchParams(window.location.search);
      p.delete('page');
      if (sel.value) { p.set('sort_by', sel.value); } else { p.delete('sort_by'); }
      window.location.search = p.toString();
    });
    anchor.parentNode.insertBefore(box, anchor);
  }

  /* 4 Oct 2026 (owner: "if I use the sort on top, the filters on the side
     go inactive"). On a collection page the search app draws the filter
     sidebar, but the theme's Sort (z__jsCollection.js, #sort-by) re-fetched
     the grid by AJAX and swapped the page's main block, which wiped the
     app's sidebar for good. The app honours ?sort_by on a normal page load
     (filters kept), so Sort loads the page with it instead. Capture phase,
     so the theme's own handler never runs on these pages. */
  document.addEventListener('change', function (ev) {
    var t = ev.target;
    if (!t || t.id !== 'sort-by' || !document.getElementById('cloud_search_filters_root')) { return; }
    ev.stopImmediatePropagation();
    ev.stopPropagation();
    var p = new URLSearchParams(window.location.search);
    p.delete('page');
    if (t.value) { p.set('sort_by', t.value); } else { p.delete('sort_by'); }
    window.location.search = p.toString();
  }, true);

  document.addEventListener('click', function (ev) {
    var t = ev.target;
    var label = t && t.closest ? t.closest('.cloud-search-filter-value') : null;
    var b = batchable(label);
    if (!b) { return; }
    ev.preventDefault();
    ev.stopPropagation();
    if (ev.stopImmediatePropagation) { ev.stopImmediatePropagation(); }
    var key = b.id + '\u0001' + b.value;
    pending[key] = !pending[key];
    var input = q('input[type="checkbox"]', label);
    var was = hasClass(label, 'cloud-search-filter-value--selected');
    var now = pending[key] ? !was : was;
    /* after the click settles: a prevented click on the box itself resets it */
    if (input) { window.setTimeout(function () { input.checked = now; }, 0); }
    label.classList.toggle('rr-pend', !!pending[key]);
    label.classList.toggle('rr-pend-on', now);
    syncApply();
  }, true);

  function boot() {
    try { appSort(); } catch (e) { /* nothing */ }
    try {
      observeMount();
      observeRootControls();
      observePanes();
      enhanceLink();
    } catch (e) { /* nothing */ }
    var tries = 0;
    var poll = function () {
      tries++;
      var done = false;
      try { done = enhanceSidebar(); } catch (e) { done = false; }
      if (!done) {
        try { observeMount(); observeRootControls(); } catch (e2) { /* nothing */ }
      }
      if (!done && tries < 80) { window.setTimeout(poll, 250); }
    };
    poll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Theme editor: sections reload without a page load */
  document.addEventListener('shopify:section:load', function () {
    window.setTimeout(function () {
      try { observeMount(); observeRootControls(); enhanceSidebar(); enhanceLink(); } catch (e) { /* nothing */ }
    }, 250);
  });

  /* Back-forward cache: the sidebar comes back as it was, the state may not */
  window.addEventListener('pageshow', function (ev) {
    if (ev && ev.persisted && enhancedOnce) {
      try { enhanceLink(); } catch (e) { /* nothing */ }
    }
  });
})();
